import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "0px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = text.length * charWidth + padding;
  return Math.min(estimatedWidth, 8 * window.innerWidth / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

const PrePostLaminationTable = ({ formName, subForm, data, subFormMappings, startDate, endDate, shifts }) => {

  // Define static headers for Observations Table
  const observationStaticHeaders = [
    { id: "subForm", label: "Sub-form" },
    { id: "tableName", label: "Table Name" },
    { id: "field", label: "Product" },
    { id: "specification", label: "Specification" },
    { id: "frequency", label: "Frequency" },
  ];

  // Define static headers for Module Inspection Table
  const moduleStaticHeaders = [
    { id: "moduleInspection", label: "Module Sr. No" },
    { id: "moduleStatus", label: "Status" },
    { id: "remarks", label: "Remarks" },
  ];

  // Reverse mapping for API subform names to UI names
  const reverseSubFormMappings = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(subFormMappings).map(([uiName, apiName]) => [apiName, uiName])
      ),
    [subFormMappings]
  );

  // Split data into observation and module data
  const observationData = data.filter((item) => item.tableName !== "ModuleInspection");
  const moduleData = data.filter((item) => item.tableName === "ModuleInspection");

  // Calculate column widths for Observations Table
  const observationColumnWidths = useMemo(() => {
    const widths = {
      subForm: estimateTextWidth("Sub-form"),
      tableName: estimateTextWidth("Table Name"),
      field: estimateTextWidth("Product"),
      specification: estimateTextWidth("Specification"),
      frequency: estimateTextWidth("Frequency"),
      obs1: 100,
      obs2: 100,
      remarks: 150,
    };

    observationData.forEach((item) => {
      if (item?.subForm) {
        const uiSubForm = reverseSubFormMappings[item.subForm] || item.subForm;
        widths.subForm = Math.min(Math.max(widths.subForm, estimateTextWidth(uiSubForm)), 8 * window.innerWidth / 100);
      }
      if (item?.tableName) {
        widths.tableName = Math.min(Math.max(widths.tableName, estimateTextWidth(item.tableName)), 8 * window.innerWidth / 100);
      }
      if (item?.field) {
        widths.field = Math.min(Math.max(widths.field, estimateTextWidth(item.field)), 8 * window.innerWidth / 100);
      }
      if (item?.specification) {
        widths.specification = Math.min(Math.max(widths.specification, estimateTextWidth(item.specification)), 8 * window.innerWidth / 100);
      }
      if (item?.frequency) {
        widths.frequency = Math.min(Math.max(widths.frequency, estimateTextWidth(item.frequency)), 8 * window.innerWidth / 100);
      }
      if (item?.obs1) {
        widths.obs1 = Math.max(widths.obs1, estimateTextWidth(item.obs1));
      }
      if (item?.obs2) {
        widths.obs2 = Math.max(widths.obs2, estimateTextWidth(item.obs2));
      }
      if (item?.remarks) {
        widths.remarks = Math.max(widths.remarks, estimateTextWidth(item.remarks));
      }
    });

    return widths;
  }, [observationData, reverseSubFormMappings]);

  // Calculate column widths for Module Inspection Table
  const moduleColumnWidths = useMemo(() => {
    const widths = {
      moduleInspection: estimateTextWidth("Module Sr. No"),
      moduleStatus: estimateTextWidth("Status"),
      remarks: estimateTextWidth("Remarks"),
    };

    moduleData.forEach((item) => {
      if (item?.moduleInspection) {
        widths.moduleInspection = Math.min(Math.max(widths.moduleInspection, estimateTextWidth(item.moduleInspection)), 8 * window.innerWidth / 100);
      }
      if (item?.moduleStatus) {
        widths.moduleStatus = Math.max(widths.moduleStatus, estimateTextWidth(item.moduleStatus));
      }
      if (item?.remarks) {
        widths.remarks = Math.max(widths.remarks, estimateTextWidth(item.remarks));
      }
    });

    return widths;
  }, [moduleData]);

  // Calculate cumulative left positions for Observations Table
  const getObservationLeftPosition = (index) => {
    const staticKeys = ["subForm", "tableName", "field", "specification", "frequency"];
    return staticKeys
      .slice(0, index)
      .reduce((sum, key) => sum + observationColumnWidths[key], 0);
  };

  // Get all dates in the range
  const dates = getDateRange(startDate, endDate);

  // Use provided shifts, fallback to data if shifts prop is empty
  const effectiveShifts = shifts.length > 0 ? shifts : [
    ...new Set(data.map((item) => item?.inspectionShift).filter(Boolean))
  ].sort();

  // Create dynamic headers for Observations Table
  const observationDynamicHeaders = dates.flatMap((date) =>
    effectiveShifts.map((shift) => ({
      subHeaders: [
        { id: `obs1_${date}_${shift}`, label: "Obs 1" },
        { id: `obs2_${date}_${shift}`, label: "Obs 2" },
        { id: `remarks_${date}_${shift}`, label: "Remarks" },
      ],
      date,
      shift,
    }))
  );

  // Create dynamic headers for Module Inspection Table
  const moduleDynamicHeaders = dates.flatMap((date) =>
    effectiveShifts.map((shift) => ({
      subHeaders: [
        { id: `moduleInspection_${date}_${shift}`, label: "Module Sr. No" },
        { id: `moduleStatus_${date}_${shift}`, label: "Status" },
        { id: `remarks_${date}_${shift}`, label: "Remarks" },
      ],
      date,
      shift,
    }))
  );

  // Group data for Observations Table
  const observationGroupedData = observationData.reduce((acc, item) => {
    if (!item.subForm || !item.tableName || !item.field) return acc;
    const uiSubform = reverseSubFormMappings[item.subForm] || item.subForm;
    const field = item.field || "N/A";
    const key = `${uiSubform}:${item.tableName}:${field}`;
    if (!acc[key]) {
      acc[key] = {
        subForm: uiSubform,
        tableName: item.tableName,
        field,
        specification: item.specification || "N/A",
        frequency: item.frequency || "N/A",
        observations: {},
      };
      dates.forEach((date) => {
        effectiveShifts.forEach((shift) => {
          const obsKey = `${date}_${shift}`;
          acc[key].observations[obsKey] = {
            obs1: "N/A",
            obs2: "N/A",
            remarks: "N/A",
          };
        });
      });
    }
    const obsKey = `${formatDate(item.inspectionDate)}_${item.inspectionShift}`;
    acc[key].observations[obsKey] = {
      obs1: item.obs1 || "N/A",
      obs2: item.obs2 || "N/A",
      remarks: item.remarks || "N/A",
    };
    return acc;
  }, {});

  // Group data for Module Inspection Table
  const moduleGroupedData = moduleData.reduce((acc, item) => {
    const date = formatDate(item.inspectionDate);
    const shift = item.inspectionShift;
    const key = `${date}_${shift}`;
    if (!acc[key]) {
      acc[key] = {
        date,
        shift,
        modules: [],
      };
    }
    acc[key].modules.push({
      moduleInspection: item.moduleInspection || "N/A",
      moduleStatus: item.moduleStatus || "N/A", // Fallback for missing moduleStatus
      remarks: item.remarks || "", // Use remarks directly, no fallback to nonConformance
    });
    return acc;
  }, {});

  // Filter data based on subForm for Observations Table
  const filteredObservationData = Object.values(observationGroupedData).filter(
    (item) => subForm.length === 0 || subForm.includes(item.subForm)
  );

  // Check condition for rendering both tables
  const isVisualInspection =
    formName === "Post-Lamination" &&
    subForm.includes("Visual Inspection (Post)");

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // === SHEET 1: Observations Table ===
    const obsHeaderRow1 = ["Sub-form", "Table Name", "Product", "Specification", "Frequency"];
    const obsHeaderRow2 = ["", "", "", "", ""];

    const merges = [];
    let colIdx = 5;

    observationDynamicHeaders.forEach((headerGroup) => {
      obsHeaderRow1.push(headerGroup.date);
      obsHeaderRow2.push(headerGroup.shift);
      merges.push({
        s: { r: 0, c: colIdx }, e: { r: 0, c: colIdx + 2 }
      });
      colIdx += 3;
    });

    const obsDataRows = filteredObservationData.map((row) => {
      const r = [
        row.subForm,
        row.tableName,
        row.field,
        row.specification,
        row.frequency,
      ];
      observationDynamicHeaders.forEach((headerGroup) => {
        const obsKey = `${headerGroup.date}_${headerGroup.shift}`;
        const obs = row.observations[obsKey] || { obs1: "N/A", obs2: "N/A", remarks: "N/A" };
        r.push(obs.obs1, obs.obs2, obs.remarks);
      });
      return r;
    });

    const obsSheetData = [obsHeaderRow1, obsHeaderRow2, ...obsDataRows];
    const ws1 = XLSX.utils.aoa_to_sheet(obsSheetData);
    if (merges.length > 0) ws1["!merges"] = merges;
    ws1["!cols"] = [
      { wch: 20 }, { wch: 18 }, { wch: 25 }, { wch: 30 }, { wch: 12 },
      ...Array(observationDynamicHeaders.length * 3).fill({ wch: 18 })
    ];
    XLSX.utils.book_append_sheet(wb, ws1, "Observations");

    // === SHEET 2: Module Inspection Table ===
    const modHeaderRow1 = ["Date", "Shift"];
    const modHeaderRow2 = ["", ""];

    let modColIdx = 2;
    const modMerges = [];

    moduleDynamicHeaders.forEach((headerGroup) => {
      modHeaderRow1.push(headerGroup.date);
      modHeaderRow2.push(headerGroup.shift);
      modMerges.push({
        s: { r: 0, c: modColIdx }, e: { r: 0, c: modColIdx + 2 }
      });
      modColIdx += 3;
    });

    // Build module rows (one row per module)
    const modDataRows = [];
    Object.values(moduleGroupedData).forEach((group) => {
      group.modules.forEach((mod, idx) => {
        const r = idx === 0 ? [group.date, group.shift] : ["", ""];
        moduleDynamicHeaders.forEach((headerGroup) => {
          const key = `${headerGroup.date}_${headerGroup.shift}`;
          const match = group.modules.find(m => 
            m.moduleInspection === mod.moduleInspection &&
            group.date === headerGroup.date &&
            group.shift === headerGroup.shift
          );
          if (match && key === `${group.date}_${group.shift}`) {
            r.push(mod.moduleInspection, mod.moduleStatus, mod.remarks);
          } else {
            r.push("", "", "");
          }
        });
        modDataRows.push(r);
      });
    });

    const modSheetData = [modHeaderRow1, modHeaderRow2, ...modDataRows];
    const ws2 = XLSX.utils.aoa_to_sheet(modSheetData);
    if (modMerges.length > 0) ws2["!merges"] = modMerges;
    ws2["!cols"] = [
      { wch: 15 }, { wch: 12 },
      ...Array(moduleDynamicHeaders.length * 3).fill({ wch: 20 })
    ];
    XLSX.utils.book_append_sheet(wb, ws2, "Module Inspection");

    // Save file
    const fileName = `${formName.replace(/\s+/g, "_")}_${formatDate(startDate || new Date())}_to_${formatDate(endDate || new Date())}.xlsx`;
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  const hasData = filteredObservationData.length > 0 || Object.keys(moduleGroupedData).length > 0;

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        {formName} Inspection Data
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 3 }}
        disabled={!hasData}
      >
        Download as Excel
      </Button>

      {/* Observations Table */}
      <Typography variant="subtitle1" gutterBottom>
        Observations Table
      </Typography>
      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto", marginBottom: "20px" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            <TableRow>
              {observationStaticHeaders.map((header, index) => (
                <StickyTableCell
                  key={header.id}
                  rowSpan={2}
                  columnWidth={observationColumnWidths[header.id]}
                  left={getObservationLeftPosition(index)}
                  isLastSticky={index === observationStaticHeaders.length - 1}
                >
                  {header.label}
                </StickyTableCell>
              ))}
              {observationDynamicHeaders.map((header) => (
                <CustomTableCell
                  key={`${header.date}_${header.shift}`}
                  colSpan={header.subHeaders.length}
                  align="center"
                  columnWidth={
                    observationColumnWidths.obs1 +
                    observationColumnWidths.obs2 +
                    observationColumnWidths.remarks
                  }
                >
                  {header.date} - {header.shift}
                </CustomTableCell>
              ))}
            </TableRow>
            <TableRow>
              {observationDynamicHeaders.map((header) =>
                header.subHeaders.map((subHeader) => (
                  <CustomTableCell
                    key={subHeader.id}
                    columnWidth={observationColumnWidths[subHeader.id.split("_")[0]]}
                  >
                    {subHeader.label}
                  </CustomTableCell>
                ))
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredObservationData.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={
                    observationStaticHeaders.length +
                    observationDynamicHeaders.reduce((sum, header) => sum + header.subHeaders.length, 0)
                  }
                  align="center"
                  index={0}
                  rowIndex={0}
                  columnWidth={observationColumnWidths.subForm}
                  left={getObservationLeftPosition(0)}
                  isLastSticky={false}
                >
                  No data available
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              filteredObservationData.map((row, index) => (
                <CustomTableRow key={index} index={index}>
                  <StickyBodyTableCell
                    index={0}
                    rowIndex={index}
                    columnWidth={observationColumnWidths.subForm}
                    left={getObservationLeftPosition(0)}
                    isLastSticky={false}
                  >
                    {row.subForm}
                  </StickyBodyTableCell>
                  <StickyBodyTableCell
                    index={1}
                    rowIndex={index}
                    columnWidth={observationColumnWidths.tableName}
                    left={getObservationLeftPosition(1)}
                    isLastSticky={false}
                  >
                    {row.tableName}
                  </StickyBodyTableCell>
                  <StickyBodyTableCell
                    index={2}
                    rowIndex={index}
                    columnWidth={observationColumnWidths.field}
                    left={getObservationLeftPosition(2)}
                    isLastSticky={false}
                  >
                    {row.field}
                  </StickyBodyTableCell>
                  <StickyBodyTableCell
                    index={3}
                    rowIndex={index}
                    columnWidth={observationColumnWidths.specification}
                    left={getObservationLeftPosition(3)}
                    isLastSticky={false}
                  >
                    {row.specification}
                  </StickyBodyTableCell>
                  <StickyBodyTableCell
                    index={4}
                    rowIndex={index}
                    columnWidth={observationColumnWidths.frequency}
                    left={getObservationLeftPosition(4)}
                    isLastSticky={true}
                  >
                    {row.frequency}
                  </StickyBodyTableCell>
                  {observationDynamicHeaders.map((header) => {
                    const obsKey = `${header.date}_${header.shift}`;
                    const obsData = row.observations[obsKey] || {};
                    return header.subHeaders.map((subHeader) => (
                      <BodyTableCell
                        key={`${subHeader.id}_${index}`}
                        columnWidth={observationColumnWidths[subHeader.id.split("_")[0]]}
                      >
                        {obsData[subHeader.id.split("_")[0]] || "N/A"}
                      </BodyTableCell>
                    ));
                  })}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Module Inspection Table (rendered only if condition is met) */}
      {isVisualInspection && (
        <>
          <Typography variant="subtitle1" gutterBottom>
            Module Inspection Table
          </Typography>
          <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
            <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
              <TableHead>
                <TableRow>
                  {moduleDynamicHeaders.map((header) => (
                    <CustomTableCell
                      key={`${header.date}_${header.shift}`}
                      colSpan={header.subHeaders.length}
                      align="center"
                      columnWidth={
                        moduleColumnWidths.moduleInspection +
                        moduleColumnWidths.moduleStatus +
                        moduleColumnWidths.remarks
                      }
                    >
                      {header.date} - {header.shift}
                    </CustomTableCell>
                  ))}
                </TableRow>
                <TableRow>
                  {moduleDynamicHeaders.map((header) =>
                    header.subHeaders.map((subHeader) => (
                      <CustomTableCell
                        key={subHeader.id}
                        columnWidth={moduleColumnWidths[subHeader.id.split("_")[0]]}
                      >
                        {subHeader.label}
                      </CustomTableCell>
                    ))
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.values(moduleGroupedData).length === 0 ? (
                  <CustomTableRow index={0}>
                    <BodyTableCell
                      colSpan={moduleDynamicHeaders.reduce((sum, header) => sum + header.subHeaders.length, 0)}
                      align="center"
                      columnWidth={moduleColumnWidths.moduleInspection}
                    >
                      No data available
                    </BodyTableCell>
                  </CustomTableRow>
                ) : (
                  // Find the maximum number of modules for any date/shift to ensure consistent row count
                  (() => {
                    const maxModules = Math.max(
                      ...Object.values(moduleGroupedData).map((group) => group.modules.length)
                    );
                    return Array.from({ length: maxModules }).map((_, rowIndex) => (
                      <CustomTableRow key={rowIndex} index={rowIndex}>
                        {moduleDynamicHeaders.map((header) => {
                          const groupKey = `${header.date}_${header.shift}`;
                          const group = moduleGroupedData[groupKey] || { modules: [] };
                          const module = group.modules[rowIndex] || {};
                          return header.subHeaders.map((subHeader) => (
                            <BodyTableCell
                              key={`${subHeader.id}_${rowIndex}`}
                              columnWidth={moduleColumnWidths[subHeader.id.split("_")[0]]}
                            >
                              {module[subHeader.id.split("_")[0]] || "N/A"}
                            </BodyTableCell>
                          ));
                        })}
                      </CustomTableRow>
                    ));
                  })()
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </div>
  );
};

export default PrePostLaminationTable;